import { useMemo, useState } from 'react';
import { PersonRemove, RemoveCircleOutline } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { useEventStore } from '@/state/stores/use-event-store';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useDeleteEntityContributor } from '@/hooks/use-delete-entity-contributor';
import { useDeleteContributorRoleSlotsByEventSlotMutation } from '@/state/mutations/contributors/use-delete-contributor-role-slots-by-event-slot';
import { useDeleteEventContributorRoles } from '@/state/mutations/contributors/use-delete-event-contributor-roles';
import { useQueryClient } from '@tanstack/react-query';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations';
import ConfirmationModal from '@/components/common/confirmation-modal';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { EVENT_SLOT_TYPE } from '@/types/dtos/events/event-slot-content.enum';
import { useAuth } from '@clerk/clerk-react';
import { isContributorLinkedToClerkUser } from '@/utils/contributor-clerk-match.utils';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { getObjectId } from '@/helpers/object.id.helper';
import { eventHasPermission, managePermissionForEventCrewRoleName } from '@/utils/event-crew-permissions';

const normalizeId = (value?: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if ('id' in value && typeof (value as { id?: string }).id === 'string') {
      return (value as { id: string }).id;
    }
    if ('_id' in value && typeof (value as { _id?: string })._id === 'string') {
      return (value as { _id: string })._id;
    }
    if ('$oid' in value && typeof (value as { $oid?: string }).$oid === 'string') {
      return (value as { $oid: string }).$oid;
    }
  }
  return undefined;
};

type CrewDeleteHeaderButtonProps = {
  entityType: 'Event' | 'Flow';
  entityId: string;
  contributor: ContributorDto;
  roleId: string;
  flow?: FlowDto;
  eventSlotIdParam?: string;
  forceMasterMode?: boolean;
};

export function CrewDeleteHeaderButton({
  entityType,
  entityId,
  contributor,
  roleId,
  flow,
  eventSlotIdParam,
  forceMasterMode,
}: CrewDeleteHeaderButtonProps) {
  const t = useTranslations();
  const { userId } = useAuth();
  const notification = useNotifications();
  const mode = useEventMode();
  const isOwnContributor = isContributorLinkedToClerkUser(contributor, userId);
  /** Host / flow-scoped rows may be removed by the same user; event crew (owner/manager) may not. */
  const blockSelfRemoveEventCrew = entityType === 'Event' && isOwnContributor;
  const [open, setOpen] = useState(false);
  const event = useEventStore((s) => s.event);
  const eventIdFromStore = event?.origin ? getObjectId(event.origin) : event?.id;
  const { data: permissions } = usePermissionsQuery();
  const eventPlanSlots = useEventStore((s) => s.eventPlan?.slots);
  const hasPlanSlotData = !!eventPlanSlots;
  const eventFlows = useEventStore((s) => s.eventFlows);
  const flowStoreFlow = useFlowStore((s) => s.flowPlan || s.flow);
  const deleteEntityContributor = useDeleteEntityContributor();
  const { mutateAsync: deleteRoleSlotsByEventSlot } = useDeleteContributorRoleSlotsByEventSlotMutation();
  const { mutateAsync: deleteContributorRoles } = useDeleteEventContributorRoles();
  const queryClient = useQueryClient();

  const flowForDelete = useMemo(() => {
    if (entityType !== 'Flow' || !flow) return undefined;
    if (flowStoreFlow && (flowStoreFlow.id === entityId || flowStoreFlow.origin === entityId)) {
      return flowStoreFlow;
    }
    return eventFlows?.find((f) => f.id === entityId || f.origin === entityId) ?? flow;
  }, [entityType, entityId, eventFlows, flow, flowStoreFlow]);

  const flowSlotId = normalizeId(flowForDelete?.slot?.id);
  const flowSlotOriginId = normalizeId(flowForDelete?.slot?.origin);
  const hasFlowSlotContext = !!(flowSlotId || flowSlotOriginId);
  const isMasterMode = forceMasterMode || (!eventSlotIdParam && !hasFlowSlotContext);

  const flowSlotCount = useMemo(() => {
    if (entityType !== 'Flow' || !eventPlanSlots) return 0;
    const flowIds = new Set<string>(
      [normalizeId(entityId), normalizeId(flowForDelete?.origin), normalizeId(flowForDelete?.id)].filter(
        Boolean,
      ) as string[],
    );
    return eventPlanSlots.filter((slot) => {
      if (slot.deleted === true) return false;
      if (slot.contentType !== EVENT_SLOT_TYPE.FLOW) return false;
      const slotContentId = normalizeId(slot.content);
      const slotContentOrigin =
        typeof slot.content === 'object' && slot.content
          ? normalizeId((slot.content as { origin?: unknown }).origin)
          : undefined;
      if (!slotContentId && !slotContentOrigin) return false;
      return (slotContentId && flowIds.has(slotContentId)) || (slotContentOrigin && flowIds.has(slotContentOrigin));
    }).length;
  }, [entityType, eventPlanSlots, entityId, flowForDelete]);

  const canDeleteFlowRole = entityType === 'Event' || isMasterMode || (hasPlanSlotData && flowSlotCount <= 1);

  const roleRow = useMemo(() => contributor.roles.find((r) => r.id === roleId), [contributor.roles, roleId]);

  const canRemoveThisEventCrew = useMemo(() => {
    if (entityType !== 'Event') return true;
    if (!roleRow || !eventIdFromStore) return false;
    return eventHasPermission(
      permissions?.events?.[eventIdFromStore],
      managePermissionForEventCrewRoleName(roleRow.role as string),
    );
  }, [entityType, roleRow, eventIdFromStore, permissions?.events]);

  const displayName =
    contributor.profiles[0]?.displayName || contributor.email || t('event.detail.contributors.unknownContributor');

  const handleConfirm = async () => {
    if (blockSelfRemoveEventCrew) {
      notification.notifyError(t('event.detail.contributors.cannotRemoveOwnRole'));
      setOpen(false);
      return;
    }
    try {
      if (entityType === 'Event') {
        await deleteContributorRoles({
          contributorId: contributor.id,
          roleIds: [roleId],
        });
        await queryClient.refetchQueries({
          queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY],
          type: 'all',
        });
        notification.notifySuccess(t('event.detail.crewWidget.removeEventRoleSuccess', { name: displayName }));
      } else if (canDeleteFlowRole) {
        await deleteEntityContributor({
          contributor,
          entityType: 'Flow',
          entityId,
        });
        await queryClient.refetchQueries({
          queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY],
          type: 'all',
        });
      } else {
        const eventSlotId = eventSlotIdParam || flowSlotId || flowSlotOriginId;
        if (!eventSlotId) {
          throw new Error('Missing event slot context for flow role removal.');
        }
        await deleteRoleSlotsByEventSlot({
          contributorId: contributor.id,
          roleId,
          eventSlotId,
        });
        await queryClient.refetchQueries({
          queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY],
          type: 'all',
        });
        notification.notifySuccess(
          t('event.detail.contributors.notifications.deleteRoleSlotSuccess', { name: displayName }),
        );
      }
      setOpen(false);
    } catch {
      if (entityType === 'Flow') {
        notification.notifyError(
          canDeleteFlowRole
            ? t('flows.detail.contributors.removeError', { name: displayName })
            : t('event.detail.contributors.notifications.deleteRoleSlotError', { name: displayName }),
        );
      } else if (entityType === 'Event') {
        notification.notifyError(t('event.detail.crewWidget.removeEventRoleError', { name: displayName }));
      }
    }
  };

  const deleteDisabled = isOrganizeMode(mode) || blockSelfRemoveEventCrew;

  if (!canRemoveThisEventCrew) {
    return null;
  }

  const deleteIconButton = (
    <IconButton
      size="small"
      color="default"
      disabled={deleteDisabled}
      data-testid="delete-crew-role-button"
      onClick={(e) => {
        e.stopPropagation();
        setOpen(true);
      }}
      sx={{
        'color': 'action.active',
        '& > svg': {
          fontSize: '1.5rem',
          width: '1.5rem',
          height: '1.5rem',
        },
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      {entityType === 'Event' || canDeleteFlowRole ? <PersonRemove /> : <RemoveCircleOutline />}
    </IconButton>
  );

  const tooltipTitle = blockSelfRemoveEventCrew
    ? t('event.detail.contributors.cannotRemoveOwnRole')
    : entityType === 'Event'
      ? t('event.detail.crewWidget.deleteEventRole', { name: displayName })
      : canDeleteFlowRole
        ? t('flows.detail.contributors.deleteTitle', { name: displayName })
        : t('event.detail.contributors.deleteRoleSlot');

  return (
    <>
      <Tooltip placement="left" disableInteractive title={tooltipTitle}>
        {deleteDisabled ? <span>{deleteIconButton}</span> : deleteIconButton}
      </Tooltip>
      <ConfirmationModal
        open={open}
        isLoading={false}
        title={
          entityType === 'Flow' && !canDeleteFlowRole
            ? t('event.detail.contributors.deleteRoleSlotModalTitle', { name: displayName })
            : entityType === 'Event'
              ? t('event.detail.crewWidget.deleteEventRoleTitle', { name: displayName })
              : t('flows.detail.contributors.deleteTitle', { name: displayName })
        }
        text={
          entityType === 'Flow' && !canDeleteFlowRole
            ? t('event.detail.contributors.deleteRoleSlotModalText', { name: displayName })
            : entityType === 'Event'
              ? t('event.detail.crewWidget.deleteEventRoleText', { name: displayName })
              : t('flows.detail.contributors.deleteText', { name: displayName })
        }
        onConfirm={handleConfirm}
        onCancel={() => setOpen(false)}
      />
    </>
  );
}
